import { Note } from 'tonal';
import {
  MAX_FRET,
  STANDARD_TUNING,
  allStrings,
  chromaToPc,
  midiAt,
  openStringMidi,
  type AccidentalPref,
  type FretRange,
  type Position,
  type Tuning,
} from './fretboard';
import { keySignature, spellInKey, type MusicalKey } from './harmony';

/**
 * Staff theory for reading (topics R1–R3, design §12).
 *
 * Conventions:
 * - Note names are **sounding** pitches, as on the fretboard: the open low E
 *   string is `E2`, so item IDs such as `staff:E4@treble8vb` and
 *   `staff→fret:G3@pos5` name the same pitch the neck plays. Guitar music is
 *   written in treble clef 8vb, an octave above how it sounds, so `E2` is
 *   written as E3.
 * - A staff position counts diatonic steps from the bottom line: 0 is the
 *   bottom line, odd numbers are spaces, 8 is the top line. Below the staff
 *   the numbers go negative (−2 is the first ledger line, middle C as written).
 *   The accidental does not move a note: B#, B and Bb share a position.
 * - A reading position is the hand position the index finger names (5th
 *   position = index at fret 5), covering four frets with one-fret stretches
 *   for the notes the four frets miss, so every pitch in it has one fret.
 */

// ---------------------------------------------------------------------------
// Clefs and staff positions

export type Clef = 'treble8vb' | 'treble';

/** Octaves the written note sits above the sounding one. */
                                                                            

                                                             
/** Diatonic index (octave × 7 + letter) of the written bottom line, E4. */
                                    

/** The five staff lines, bottom first. */
                                                              
                          

/** A note placed on a staff. */
export interface StaffNote {
  /** Sounding pitch, scientific name: `E2`, `F#3`. */
  readonly name: string;
  /** Written pitch (an octave up in treble 8vb): `E3`, `F#4`. */
  readonly written: string;
  /** Sounding MIDI number. */
  readonly midi: number;
  readonly letter: string;
  /** Accidental as a number: 1 = sharp, −1 = flat, 2 = double sharp. */
  readonly alt: number;
  readonly position: number;
  readonly ledgerLines: number;
}

                                                                                              
                           
                                                          
                                                               
   
                                                                    
 

                                              
                                                       
 

                                            
                           
 

/** Staff position of a sounding note: `E2` → −7 in treble 8vb (below three ledger lines). */
                                                                               
                                          
               
                                                                                         
                                   
 

/** The sounding note at a staff position, with an accidental (0 = natural). */
                                    
                   
                           
                  
           
                                                                                                   
                                             
                                                  
                                                              
                                                 
 

                                                   
                                
 

                                                      
                                               
 

/**
 * Ledger lines a note needs: the lines between the staff and the note, plus
 * its own line if it sits on one. `E2` (−7) needs 3 in treble 8vb.
 */
                                                           
                                                     
                                                                        
           
 

/** Positions of the ledger lines to draw for a note, nearest the staff first. */
                                                                 
                                          
                                     
                                           
                                                                            
 

                                     
                                              
                                                                          
                         
 

/**
 * Spoken name of a staff position, lines and spaces counted from the bottom:
 * "2nd line", "3rd space", "space below the staff", "2nd ledger line below",
 * "space above the 1st ledger line".
 */
                                                                 
                            
                           
                                           
                                               
   
                                                
                                          
                                                                       
                    
                               
                                                        
 

/**
 * Every position from the space beyond `maxLedgers` ledger lines below the
 * staff to the space beyond them above, lowest first. R1 reads ±3 ledger
 * lines: −7 (low E in treble 8vb) to 15.
 */
                                                                  
                                                        
                                                                                      
   
                                       
                                                
                                                                            
 

/** A sounding note with everything a staff needs to draw it. */
                                                                              
                                                     
                                             
                                  
          
                                  
                                                               
         
           
        
             
                                           
    
 

// ---------------------------------------------------------------------------
// Key signatures and accidentals

/** The accidental a key signature gives a letter: F in G major → 1 (sharp). */
                                                                          
                                 
                                                                                                
                                                                               
                                      
                                              
                                     
 

/**
 * The note a key signature makes of an unaltered note: `F3` in G major →
 * `F#3`, `B` in F major → `Bb`. Takes a pitch class or a note with an octave.
 */
                                                                          
                           
                                                        
                                                                     
                                                                                      
 

/**
 * Spell a sounding MIDI pitch for a key: diatonic notes take the key's
 * spelling, chromatic ones its flattened degrees (or #4), as `spellInKey`.
 * The octave follows the letter, so 60 in C# major is `B#3`.
 */
                                                                       
                                            
                                          
                                                    
                                                                
   
                                                        
 

/** Spell a sounding MIDI pitch with no key: sharps or flats by preference. */
                                                                                 
                                                                          
 

export type AccidentalMark = 'double-flat' | 'flat' | 'natural' | 'sharp' | 'double-sharp';

                                               
                      
               
               
             
                    
  

/** The sign an accidental is written with: 0 → natural, −1 → flat. */
                                                             
                          
                                                                                
              
 

/**
 * The accidentals to write for one bar of notes in a key, one per note, null
 * where none is needed. A note needs a sign when its accidental differs from
 * what the key signature, or an earlier accidental on the same staff position
 * in this bar, already gives it. An accidental lasts to the barline and holds
 * only for its own position, not other octaves.
 */
                               
                           
                  
                           
                              
                                            
                              
                                            
                                               
                                                                           
                                      
                               
                               
     
 

/** The accidental a single note needs in a key, at the start of a bar. */
                                                                                       
                                                                 
 

/**
 * Where the signs of a key signature sit, in drawing order, as written
 * treble-clef positions (the same in treble 8vb): sharps F C G D A E B from
 * the top line, flats B E A D G C F from the middle line.
 */
                                                                  
                                                        
                                          
                   
                                                    
                                                     
                                 
                               
                                                                             
     
 

// ---------------------------------------------------------------------------
// Reading positions (R2)

export type ReadingPositionId = 'open' | `pos${number}`;

export interface ReadingPosition {
  readonly id: ReadingPositionId;
  /** The fret the index finger covers; 0 for open position. */
  readonly fret: number;
  readonly label: string;
  /** The four frets the hand covers without stretching: 0–3 in open position. */
  readonly frets: FretRange;
}

/** Which finger stretches out of the position for a note the four frets miss. */
export type Stretch = 'index' | 'pinky' | 'none';

export interface ReadingOptions {
  readonly tuning?: Tuning;
  /**
   * Preferred stretch: `index` reaches back a fret (C# on string 5, fret 4 in
   * 5th position), `pinky` forward a fret. The other is the fallback, as in
   * open position, where only the pinky can stretch (to fret 4).
   * `none` keeps to the four frets. Default `index`.
   */
  readonly stretch?: Stretch;
}

/** A position by its index-finger fret: 0 → open, 5 → 5th position. */
                                                                
                                                                   
                                                                
   
                   
                                                                             
       
                         
             
                                           
                                            
        
 

/** `open`, `pos5` → position. Throws on anything else. */
export function parseReadingPosition(id: string): ReadingPosition {
  if (id === 'open') return readingPosition(0);
  const m = /^pos([1-9]\d*)$/.exec(id);
  if (!m) throw new Error(`Invalid reading position: ${id}`);
  return readingPosition(Number(m[1]));
}

/** The positions R2 teaches, in order (research/03 R2). */
                                                                                                   

/** Frets the hand can reach in a position, stretches included: 4–9 in 5th position. */
                                                                                                
                                                     
          
                                               
                                                  
    
 

                                                                              
                                        
                                      
                                                                                                
                                                      
 

/**
 * The one string and fret that plays a sounding pitch in a position, or null
 * when the hand can't reach it. A fret inside the four-fret span wins (open B
 * stays the open string, not string 3 fret 4); otherwise the preferred
 * stretch, then the other one. Among equals the higher-pitched string wins,
 * which only matters in tunings with strings closer than a major 3rd apart.
 */
                               
               
                            
                            
                    
                                                
                             
                                                                               
                                                                 
                             
                                              
                                                                                  
     
   
              
 

/** As `fretInPosition`, from a note name (`G3`). */
                            
               
                            
                            
                    
                                                              
 

/**
 * Every pitch the position can play, low to high, each with its one fret.
 * The gaps a four-fret span leaves between strings a 4th apart are filled by
 * the stretch, so standard tuning gives an unbroken chromatic run.
 */
                            
                            
                            
                                     
                                                
                                              
                                    
                                            
                                                         
                                                    
   
                                                   
                                                          
                                                    
                                   
   
             
 

/** True if this cell is the one the position plays its pitch on. */
                               
                
                            
                            
            
                                                                                         
                                                                         
 

/**
 * The staff note for a fretted cell: spelled for the key when there is one,
 * otherwise by the sharp/flat preference.
 */
                            
                
                                                                                       
              
                                                           
                                                                                      
                                    
 

import { readingPosition } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/staff.ts?split=27";
